import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import {
    Package, Plus, Edit3, Trash2, ToggleLeft, ToggleRight,
    X, IndianRupee, Briefcase, CheckCircle, Tag
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';
import { useConfirm } from '../../context/ConfirmContext';
import API_BASE_URL from '../../config/api';

const FRANCHISE_TYPES = [
    { value: 'city', label: 'City Franchise', color: 'blue' },
    { value: 'district', label: 'District Franchise', color: 'purple' },
    { value: 'state', label: 'State Franchise', color: 'orange' },
    { value: 'super', label: 'Super Franchise', color: 'red' },
];

const emptyForm = {
    package_name: '',
    franchise_type: 'city',
    package_price: '',
    epin_count: '',
    stock_value: '',
    commission_percent: '',
    description: '',
    benefits: '',
    is_active: 1,
};

const FranchisePackages = () => {
    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({ ...emptyForm });
    const [saving, setSaving] = useState(false);

    const fetchPackages = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/franchise_packages.php`);
            const data = await response.json();
            if (data.success) {
                setPackages(data.packages || []);
            }
        } catch (error) {
            console.error('Error fetching packages:', error);
            showAlert('Failed to load packages', 'error');
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    useEffect(() => {
        fetchPackages();
    }, [fetchPackages]);

    const openCreateModal = () => {
        setFormData({ ...emptyForm });
        setEditMode(false);
        setShowModal(true);
    };

    const openEditModal = (pkg) => {
        setFormData({
            id: pkg.id,
            package_name: pkg.package_name || '',
            franchise_type: pkg.franchise_type || 'city',
            package_price: pkg.package_price || '',
            epin_count: pkg.epin_count || '',
            stock_value: pkg.stock_value || '',
            commission_percent: pkg.commission_percent || '',
            description: pkg.description || '',
            benefits: pkg.benefits || '',
            is_active: pkg.is_active,
        });
        setEditMode(true);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.package_name || !formData.package_price) {
            showAlert('Package name and price are required', 'error');
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/franchise_packages.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    action: editMode ? 'update' : 'create',
                    ...formData
                })
            });

            const data = await response.json();
            if (data.success) {
                showAlert(data.message, 'success');
                setShowModal(false);
                fetchPackages();
            } else {
                showAlert(data.message || 'Operation failed', 'error');
            }
        } catch (error) {
            showAlert('Server error', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id, name) => {
        const confirmed = await showConfirm(
            'Delete Package',
            `Are you sure you want to delete "${name}"? This action cannot be undone.`,
            'danger'
        );
        if (!confirmed) return;

        try {
            const response = await fetch(`${API_BASE_URL}/admin/franchise_packages.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'delete', id })
            });
            const data = await response.json();
            if (data.success) {
                showAlert('Package deleted', 'success');
                fetchPackages();
            } else {
                showAlert(data.message, 'error');
            }
        } catch (error) {
            showAlert('Failed to delete', 'error');
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/franchise_packages.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'toggle_status', id })
            });
            const data = await response.json();
            if (data.success) {
                fetchPackages();
            }
        } catch (error) {
            showAlert('Failed to toggle status', 'error');
        }
    };

    const getTypeConfig = (type) => {
        return FRANCHISE_TYPES.find(t => t.value === type) || FRANCHISE_TYPES[0];
    };



    return (
        <Layout>
            <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <Package className="text-orange-600" size={32} />
                            <h1 className="text-3xl font-bold text-gray-900">Franchise Packages</h1>
                        </div>
                        <p className="text-gray-600">Manage franchise package types, pricing, and benefits</p>
                    </div>
                    <button
                        onClick={openCreateModal}
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-amber-500 text-white rounded-xl hover:from-orange-700 hover:to-amber-600 transition-all shadow-lg shadow-orange-200 font-semibold"
                    >
                        <Plus size={20} />
                        Add New Package
                    </button>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-white p-4 rounded-2xl border-2 border-gray-100 shadow-sm">
                        <p className="text-xs font-bold text-gray-500 uppercase">Total</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{packages.length}</p>
                    </div>
                    {FRANCHISE_TYPES.map(ft => (
                        <div key={ft.value} className={`bg-white p-4 rounded-2xl border-2 border-${ft.color}-100 shadow-sm`}>
                            <p className="text-xs font-bold text-gray-500 uppercase">{ft.label}</p>
                            <p className={`text-2xl font-bold text-${ft.color}-600 mt-1`}>
                                {packages.filter(p => p.franchise_type === ft.value).length}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Packages Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
                    </div>
                ) : packages.length === 0 ? (
                    <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-16 text-center">
                        <Package className="mx-auto text-gray-300 mb-4" size={64} />
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No Packages Created Yet</h3>
                        <p className="text-gray-500 mb-6">Create your first franchise package to get started</p>
                        <button
                            onClick={openCreateModal}
                            className="px-6 py-3 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition-colors"
                        >
                            Create First Package
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {packages.map((pkg) => {
                            const typeConfig = getTypeConfig(pkg.franchise_type);
                            return (
                                <div
                                    key={pkg.id}
                                    className={`bg-white rounded-2xl shadow-lg border-2 ${pkg.is_active === 1 || pkg.is_active === '1' ? 'border-gray-100' : 'border-red-100 opacity-70'} overflow-hidden hover:shadow-xl transition-all duration-300`}
                                >
                                    {/* Card Header */}
                                    <div className={`p-5 bg-gradient-to-r ${pkg.franchise_type === 'city' ? 'from-blue-500 to-blue-600' :
                                        pkg.franchise_type === 'district' ? 'from-purple-500 to-purple-600' :
                                            pkg.franchise_type === 'state' ? 'from-orange-500 to-orange-600' :
                                                'from-red-500 to-red-600'
                                        }`}>
                                        <div className="flex items-center justify-between">
                                            <span className="bg-white/20 text-white px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider">
                                                {typeConfig.label}
                                            </span>
                                            <div className="flex items-center gap-1">
                                                {(pkg.is_active === 1 || pkg.is_active === '1') ? (
                                                    <span className="bg-green-400/30 text-white px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase flex items-center gap-1">
                                                        <CheckCircle size={10} /> Active
                                                    </span>
                                                ) : (
                                                    <span className="bg-red-400/30 text-white px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase">
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <h3 className="text-xl font-bold text-white mt-3">{pkg.package_name}</h3>
                                    </div>

                                    {/* Card Body */}
                                    <div className="p-5 space-y-4">
                                        {/* Price */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-500 font-semibold">Package Price</span>
                                            <span className="text-2xl font-bold text-gray-900">₹{parseFloat(pkg.package_price).toLocaleString('en-IN')}</span>
                                        </div>

                                        {/* Details Grid */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-gray-50 rounded-xl p-3">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">E-Pins</p>
                                                <p className="text-lg font-bold text-gray-900">{pkg.epin_count || 0}</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-xl p-3">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Stock Value</p>
                                                <p className="text-lg font-bold text-gray-900">₹{parseFloat(pkg.stock_value || 0).toLocaleString('en-IN')}</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Commission</p>
                                                <p className="text-lg font-bold text-green-600">{pkg.commission_percent || 0}%</p>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        {pkg.description && (
                                            <div className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Description</p>
                                                <p className="line-clamp-2">{pkg.description}</p>
                                            </div>
                                        )}

                                        {/* Actions */}
                                        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                                            <button
                                                onClick={() => openEditModal(pkg)}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-blue-50 text-blue-600 rounded-xl text-xs font-bold hover:bg-blue-100 transition-colors"
                                            >
                                                <Edit3 size={14} /> Edit
                                            </button>
                                            <button
                                                onClick={() => handleToggleStatus(pkg.id)}
                                                className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold transition-colors ${(pkg.is_active === 1 || pkg.is_active === '1')
                                                    ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                                                    : 'bg-green-50 text-green-600 hover:bg-green-100'
                                                    }`}
                                            >
                                                {(pkg.is_active === 1 || pkg.is_active === '1') ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                                                {(pkg.is_active === 1 || pkg.is_active === '1') ? 'Disable' : 'Enable'}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(pkg.id, pkg.package_name)}
                                                className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-50 text-red-600 rounded-xl text-xs font-bold hover:bg-red-100 transition-colors"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-amber-500 text-white p-6 rounded-t-2xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {editMode ? <Edit3 size={24} /> : <Plus size={24} />}
                                    <h2 className="text-xl font-bold">
                                        {editMode ? 'Edit Package' : 'Create New Package'}
                                    </h2>
                                </div>
                                <button onClick={() => setShowModal(false)} className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors">
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {/* Package Name */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    <Tag size={14} className="inline mr-1" /> Package Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.package_name}
                                    onChange={e => setFormData({ ...formData, package_name: e.target.value })}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                                    placeholder="e.g. City Starter, District Pro..."
                                    required
                                />
                            </div>

                            {/* Type & Price */}
                            <div className="grid md:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        <Briefcase size={14} className="inline mr-1" /> Franchise Type *
                                    </label>
                                    <select
                                        value={formData.franchise_type}
                                        onChange={e => setFormData({ ...formData, franchise_type: e.target.value })}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                                        required
                                    >
                                        {FRANCHISE_TYPES.map(ft => (
                                            <option key={ft.value} value={ft.value}>{ft.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        <IndianRupee size={14} className="inline mr-1" /> Package Price (₹) *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.package_price}
                                        onChange={e => setFormData({ ...formData, package_price: e.target.value })}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                                        placeholder="50000"
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>
                            </div>

                            {/* E-Pin Count & Stock Value */}
                            <div className="grid md:grid-cols-3 gap-5">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">E-Pin Count</label>
                                    <input
                                        type="number"
                                        value={formData.epin_count}
                                        onChange={e => setFormData({ ...formData, epin_count: e.target.value })}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                                        placeholder="100"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Stock Value (₹)</label>
                                    <input
                                        type="number"
                                        value={formData.stock_value}
                                        onChange={e => setFormData({ ...formData, stock_value: e.target.value })}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                                        placeholder="25000"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Commission %</label>
                                    <input
                                        type="number"
                                        value={formData.commission_percent}
                                        onChange={e => setFormData({ ...formData, commission_percent: e.target.value })}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                                        placeholder="10"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                                    placeholder="Brief description of this franchise package..."
                                    rows="2"
                                />
                            </div>

                            {/* Benefits */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Benefits (comma-separated)</label>
                                <textarea
                                    value={formData.benefits}
                                    onChange={e => setFormData({ ...formData, benefits: e.target.value })}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                                    placeholder="Exclusive territory rights, Priority support, Training included..."
                                    rows="2"
                                />
                            </div>

                            {/* Active Toggle */}
                            <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-xl">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, is_active: formData.is_active ? 0 : 1 })}
                                    className={`relative w-12 h-6 rounded-full transition-colors ${formData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
                                >
                                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                </button>
                                <span className="text-sm font-semibold text-gray-700">
                                    {formData.is_active ? 'Active — visible to users' : 'Inactive — hidden from users'}
                                </span>
                            </div>

                            {/* Submit */}
                            <div className="flex gap-4 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-[2] py-3 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {saving ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            {editMode ? <Edit3 size={16} /> : <Plus size={16} />}
                                            {editMode ? 'Update Package' : 'Create Package'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default FranchisePackages;
